import { Badge } from '@/components/ui/badge'
import { AlertCircle } from 'lucide-react'

/**
 * Help content specific to social posts
 */
export function SocialPostHelp() {
    return (
        <>
            <h3 className="font-medium text-lg mb-2">Publishing Social Media Content</h3>
            <div className="space-y-5">
                <div>
                    <h4 className="font-medium text-base mb-2">Publishing Options</h4>
                    <div className="bg-secondary/30 p-4 rounded-md border border-border mb-4">
                        <ul className="grid gap-3">
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 1</Badge>
                                <div>
                                    <span className="font-medium">Copy Content:</span> Copy the formatted text content for pasting into social platforms
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 2</Badge>
                                <div>
                                    <span className="font-medium">Web Preview:</span> View and share as a standalone webpage
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 3</Badge>
                                <div>
                                    <span className="font-medium">Multi-Platform:</span> Adapt the content for different social networks
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <h4 className="font-medium text-base mb-2">Step-by-step Publishing Guide</h4>
                    <ol className="list-decimal pl-5 space-y-3 mb-4">
                        <li>
                            <span className="font-medium">Finalize your content:</span> Ensure your text, hashtags, and messaging are ready
                            <div className="pl-5 mt-1 text-sm text-muted-foreground">
                                <p>Use the <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> feature to refine your social media post</p>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Set status to Published:</span> Open the content settings and change status to 
                            <Badge className="ml-1 mr-1 font-normal">Published</Badge>
                            <div className="pl-5 mt-1 text-sm">
                                <p className="text-muted-foreground">This marks the content as ready for distribution and enables all sharing features</p>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Choose your publishing method:</span>
                            <div className="pl-5 mt-1 space-y-2">
                                <div>
                                    <p className="font-medium text-sm">For copying text content:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Select <span className="font-semibold">Copy Markdown</span> or <span className="font-semibold">Copy Preview</span></li>
                                        <li>Paste into your social media platform or scheduling tool</li>
                                    </ol>
                                </div>
                                <div>
                                    <p className="font-medium text-sm">For sharing as a webpage:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Click <span className="font-semibold">Open as Webpage</span></li>
                                        <li>Share the URL with your audience or on platforms that support link sharing</li>
                                    </ol>
                                </div>
                                <div>
                                    <p className="font-medium text-sm">For adapting to multiple platforms:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>First, copy the content using the <span className="font-semibold">Copy Preview</span> option</li>
                                        <li>Adjust formatting, length, and hashtags according to each platform's best practices</li>
                                        <li>Schedule or post content to each platform individually</li>
                                    </ol>
                                </div>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Optimize for engagement:</span> Customize content for each platform
                            <div className="pl-5 mt-1 text-sm text-muted-foreground">
                                <p>Add relevant hashtags, mentions, and calls-to-action to increase visibility and engagement</p>
                            </div>
                        </li>
                    </ol>
                </div>

                <div>
                    <h4 className="font-medium text-base mb-2">Making Updates After Publishing</h4>
                    <div className="bg-secondary/30 p-4 rounded-md border border-border mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                            <li>Return to this page and make your desired changes</li>
                            <li>Use <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> or edit directly</li>
                            <li>A new version will be created automatically</li>
                            <li>For already posted content, you'll need to manually update each post on your social platforms</li>
                            <li>For scheduled but not yet posted content, replace with the updated version</li>
                        </ol>
                    </div>
                </div>
                    
                <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-md border border-amber-200 dark:border-amber-800 mb-4">
                    <h4 className="font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4" />
                        Social Media Best Practices
                    </h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                        <li>Tailor content length for each platform (Twitter: 280 chars, Instagram: focus on visuals)</li>
                        <li>Use relevant hashtags but don't overload - 2-3 for Twitter, 5-10 for Instagram</li>
                        <li>Include a clear call-to-action in every post</li>
                        <li>Consider timing - post when your audience is most active</li>
                        <li>Add visual elements when possible to increase engagement</li>
                        <li>For link sharing, use UTM parameters to track performance</li>
                    </ul>
                </div>
            </div>
        </>
    )
}